import { checkins } from "./data/checkins";
import type { Checkin, Group } from "~/types";
import { createServerFn } from "@tanstack/react-start";
import { groups, getGroupedCheckins } from "~/helpers/groups";
import { filterCheckin } from "~/helpers/filter-checkin";

import z from "zod";

type CheckinResult = {
  checkins: Checkin[];
  totalCheckins: number;
  currentPage: number;
  totalPages: number;
  totalCountries: number;
  totalVenues: number;
  nextPage: number | undefined;
  prevPage: number | undefined;
  params: {
    page: number;
    group?: Group["group"];
    groupValue?: string;
    limit: number;
  };
};

const GetCheckinsSchema = z.object({
  page: z.number().optional().default(1),
  group: z.enum(groups.map((g) => g.group)).optional(),
  groupValue: z.string().optional(),
});

const limit = 100;
export const getCheckins = createServerFn()
  .inputValidator(GetCheckinsSchema)
  .handler(
    async ({
      data: { page = 1, group, groupValue },
    }): Promise<CheckinResult> => {
      let checkinsData: Checkin[] = checkins;
      if (group) {
        checkinsData = getGroupedCheckins(checkinsData, group, groupValue);
      }

      // Compute total counts before filtering and paginating the data
      const totalCheckins = checkinsData.length;
      const totalCountries = checkinsData.reduce((acc, checkin) => {
        if (checkin.venue?.location?.country)
          acc.add(checkin.venue.location.country);
        return acc;
      }, new Set<string>()).size;
      const totalVenues = checkinsData.reduce((acc, checkin) => {
        if (checkin.venue?.id) acc.add(checkin.venue.id);
        return acc;
      }, new Set<string>()).size;

      // Filter out checkins with bad or missing content
      checkinsData = checkinsData.filter(filterCheckin);

      // Pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedCheckins = checkinsData.slice(startIndex, endIndex);

      return {
        checkins: paginatedCheckins,
        totalCheckins,
        totalCountries,
        totalVenues,
        currentPage: page,
        totalPages: Math.ceil(totalCheckins / limit),
        nextPage: endIndex < totalCheckins ? page + 1 : undefined,
        prevPage: page > 1 ? page - 1 : undefined,
        params: {
          limit,
          page,
          group,
          groupValue,
        },
      };
    }
  );

const GetCheckinByIdSchema = z.object({
  id: z.string(),
});

export const getCheckinById = createServerFn()
  .inputValidator(GetCheckinByIdSchema)
  .handler(
    async ({
      data: { id },
    }): Promise<{ checkin: Checkin; params: { id: string } }> => {
      const checkin = checkins.find((c) => c.id === id);
      if (!checkin) {
        throw new Error("Checkin not found");
      }

      return { checkin, params: { id } };
    }
  );
